import React, {useEffect, useRef, useState, useCallback} from "react";

type Shape = {
    id: string;
    type: "circle" | "polygon" | "line";
    color: string;
} & (
    | {type: "circle"; center: [number, number]; radiusKm: number}
    | {type: "polygon" | "line"; vertices: Array<[number, number]>}
);

type DrawingTool = "circle" | "polygon" | "line" | "none";

// Haversine distance formula
const EARTH_RADIUS_KM = 6371;

function haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const toRad = (deg: number) => deg * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return EARTH_RADIUS_KM * c;
}

// Helper: Check if a point is inside a circle using haversine
function isPointInCircle(pointLat: number, pointLng: number, centerLat: number, centerLng: number, radiusKm: number): boolean {
    const distance = haversineDistance(centerLat, centerLng, pointLat, pointLng);
    return distance <= radiusKm;
}

// Draw circle using pixel-by-pixel fill algorithm
function drawCircleWithFill(
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    centerLat: number,
    centerLng: number,
    radiusKm: number,
    color: string,
    opacity = "80"
) {
    // Calculate bounding box for the circle
    const angularDistance = radiusKm / EARTH_RADIUS_KM;
    const angularDistanceDeg = angularDistance * 180 / Math.PI;

    // Clamp latitude bounds
    const maxLat = Math.min(90, centerLat + angularDistanceDeg);
    const minLat = Math.max(-90, centerLat - angularDistanceDeg);

    // For longitude, we need to consider if the circle wraps around
    // At high latitudes, circles can span more longitude degrees
    const latRad = centerLat * Math.PI / 180;
    const lngSpan = angularDistanceDeg / Math.max(0.1, Math.cos(latRad));

    // Sample the canvas at a reasonable resolution
    const sampleRate = 2; // Check every N pixels

    // Create an ImageData to write directly to
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    // Parse color
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    const a = parseInt(opacity, 16);

    for (let y = 0; y < canvas.height; y += sampleRate) {
        const lat = 90 - (y / canvas.height) * 180;

        // Skip if outside latitude bounds
        if (lat > maxLat || lat < minLat) continue;

        for (let x = 0; x < canvas.width; x += sampleRate) {
            const lng = (x / canvas.width) * 360 - 180;

            // Check if this pixel is inside the circle
            if (isPointInCircle(lat, lng, centerLat, centerLng, radiusKm)) {
                // Fill this pixel and nearby pixels in the sample block
                for (let dy = 0; dy < sampleRate && y + dy < canvas.height; dy++) {
                    for (let dx = 0; dx < sampleRate && x + dx < canvas.width; dx++) {
                        const pixelIndex = ((y + dy) * canvas.width + (x + dx)) * 4;

                        // Alpha blend with existing pixel
                        const existingR = data[pixelIndex];
                        const existingG = data[pixelIndex + 1];
                        const existingB = data[pixelIndex + 2];
                        const existingA = data[pixelIndex + 3];

                        const alpha = a / 255;
                        data[pixelIndex] = r * alpha + existingR * (1 - alpha);
                        data[pixelIndex + 1] = g * alpha + existingG * (1 - alpha);
                        data[pixelIndex + 2] = b * alpha + existingB * (1 - alpha);
                        data[pixelIndex + 3] = Math.max(existingA, a);
                    }
                }
            }
        }
    }

    ctx.putImageData(imageData, 0, 0);
}

// Generate circle points for outline (still used for preview/editing)
function generateCirclePoints(centerLat: number, centerLng: number, radiusKm: number, segments = 256): Array<[number, number]> {
    const points: Array<[number, number]> = [];
    const angularDistance = radiusKm / EARTH_RADIUS_KM;

    for (let i = 0; i <= segments; i++) {
        const bearing = (i / segments) * 360;
        const bearingRad = bearing * Math.PI / 180;

        const lat1 = centerLat * Math.PI / 180;
        const lng1 = centerLng * Math.PI / 180;

        const lat2 = Math.asin(Math.sin(lat1) * Math.cos(angularDistance) +
                               Math.cos(lat1) * Math.sin(angularDistance) * Math.cos(bearingRad));

        const lng2 = lng1 + Math.atan2(Math.sin(bearingRad) * Math.sin(angularDistance) * Math.cos(lat1),
                                       Math.cos(angularDistance) - Math.sin(lat1) * Math.sin(lat2));

        let lat2Deg = lat2 * 180 / Math.PI;
        let lng2Deg = lng2 * 180 / Math.PI;

        // Normalize longitude to -180 to 180
        while (lng2Deg > 180) lng2Deg -= 360;
        while (lng2Deg < -180) lng2Deg += 360;

        // Clamp latitude
        lat2Deg = Math.max(-90, Math.min(90, lat2Deg));

        points.push([lng2Deg, lat2Deg]);
    }

    return points;
}

export function SimpleDrawPage() {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [shapes, setShapes] = useState<Shape[]>([]);
    const [activeTool, setActiveTool] = useState<DrawingTool>("none");
    const [selectedColor, setSelectedColor] = useState("#3B82F6");
    const [currentDrawing, setCurrentDrawing] = useState<Array<[number, number]>>([]);
    const [mousePos, setMousePos] = useState<[number, number] | null>(null);

    // Convert lat/lng to canvas coordinates
    const latLngToCanvas = useCallback((lng: number, lat: number, canvas: HTMLCanvasElement, worldOffset = 0): [number, number] => {
        const adjustedLng = lng + (worldOffset * 360);
        // Normalize to -180 to 180 for single world view
        let normalizedLng = ((adjustedLng + 180) % 360) - 180;
        if (normalizedLng < -180) normalizedLng += 360;

        const x = ((normalizedLng + 180) / 360) * canvas.width;
        const y = ((90 - lat) / 180) * canvas.height;
        return [x, y];
    }, []);

    // Convert canvas coordinates to lat/lng
    const canvasToLatLng = useCallback((x: number, y: number, canvas: HTMLCanvasElement): [number, number] => {
        const lng = (x / canvas.width) * 360 - 180;
        const lat = 90 - (y / canvas.height) * 180;
        return [lng, lat];
    }, []);

    // Draw function
    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        // Clear canvas
        ctx.fillStyle = "#f0f0f0";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw grid
        ctx.strokeStyle = "#ddd";
        ctx.lineWidth = 1;
        for (let i = 0; i <= 360; i += 30) {
            const x = (i / 360) * canvas.width;
            ctx.beginPath();
            ctx.moveTo(x, 0);
            ctx.lineTo(x, canvas.height);
            ctx.stroke();
        }
        for (let i = 0; i <= 180; i += 30) {
            const y = (i / 180) * canvas.height;
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(canvas.width, y);
            ctx.stroke();
        }

        // Helper to draw shape at multiple world offsets with clipping
        const drawShapeWithClipping = (
            ctx: CanvasRenderingContext2D,
            points: Array<[number, number]>,
            closed = true
        ) => {
            if (points.length === 0) return;

            // Draw at offsets -1, 0, +1 (left world, center world, right world)
            for (let worldOffset = -1; worldOffset <= 1; worldOffset++) {
                ctx.beginPath();

                for (let i = 0; i < points.length; i++) {
                    const [lng, lat] = points[i];

                    // Apply world offset to longitude
                    const adjustedLng = lng + (worldOffset * 360);

                    const x = ((adjustedLng + 180) / 360) * canvas.width;
                    const y = ((90 - lat) / 180) * canvas.height;

                    if (i === 0) {
                        ctx.moveTo(x, y);
                    } else {
                        ctx.lineTo(x, y);
                    }
                }

                if (closed) {
                    ctx.closePath();
                }
                ctx.fill();
                ctx.stroke();
            }
        };

        // Draw completed shapes
        for (const shape of shapes) {
            if (shape.type === "circle") {
                // Use pixel fill algorithm for circles - much more accurate!
                drawCircleWithFill(ctx, canvas, shape.center[1], shape.center[0], shape.radiusKm, shape.color, "80");

                // Draw outline
                ctx.strokeStyle = shape.color;
                ctx.lineWidth = 2;
                const points = generateCirclePoints(shape.center[1], shape.center[0], shape.radiusKm);
                if (points.length > 0) {
                    drawShapeWithClipping(ctx, points, false);
                }
            } else {
                ctx.fillStyle = shape.color + "80"; // 50% opacity
                ctx.strokeStyle = shape.color;
                ctx.lineWidth = 2;

                if (shape.type === "polygon") {
                    if (shape.vertices.length >= 3) {
                        drawShapeWithClipping(ctx, shape.vertices, true);
                    }
                } else if (shape.type === "line") {
                    if (shape.vertices.length >= 2) {
                        drawShapeWithClipping(ctx, shape.vertices, false);
                    }
                }
            }
        }

        // Draw preview
        if (currentDrawing.length > 0 && activeTool !== "none") {
            if (activeTool === "circle" && currentDrawing.length === 1 && mousePos) {
                const radiusKm = haversineDistance(
                    currentDrawing[0][1], currentDrawing[0][0],
                    mousePos[1], mousePos[0]
                );

                // Use pixel fill for preview too
                drawCircleWithFill(ctx, canvas, currentDrawing[0][1], currentDrawing[0][0], radiusKm, selectedColor, "40");

                // Draw dashed outline
                ctx.strokeStyle = selectedColor;
                ctx.lineWidth = 2;
                ctx.setLineDash([5, 5]);
                const points = generateCirclePoints(currentDrawing[0][1], currentDrawing[0][0], radiusKm);
                if (points.length > 0) {
                    drawShapeWithClipping(ctx, points, false);
                }
                ctx.setLineDash([]);
            } else if (activeTool === "polygon" || activeTool === "line") {
                ctx.fillStyle = selectedColor + "40"; // 25% opacity
                ctx.strokeStyle = selectedColor;
                ctx.lineWidth = 2;
                ctx.setLineDash([5, 5]);

                const previewVertices = mousePos ? [...currentDrawing, mousePos] : currentDrawing;
                if (activeTool === "polygon" && previewVertices.length >= 2) {
                    drawShapeWithClipping(ctx, previewVertices, true);
                } else if (activeTool === "line" && previewVertices.length >= 1) {
                    drawShapeWithClipping(ctx, previewVertices, false);
                }

                ctx.setLineDash([]);
            }
        }

        // Draw current drawing points at all world offsets
        ctx.fillStyle = selectedColor;
        for (const [lng, lat] of currentDrawing) {
            for (let worldOffset = -1; worldOffset <= 1; worldOffset++) {
                const adjustedLng = lng + (worldOffset * 360);
                const x = ((adjustedLng + 180) / 360) * canvas.width;
                const y = ((90 - lat) / 180) * canvas.height;

                ctx.beginPath();
                ctx.arc(x, y, 4, 0, Math.PI * 2);
                ctx.fill();
            }
        }
    }, [shapes, currentDrawing, mousePos, activeTool, selectedColor, latLngToCanvas]);

    // Redraw when things change
    useEffect(() => {
        draw();
    }, [draw]);

    // Handle canvas resize
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const resizeCanvas = () => {
            const rect = canvas.getBoundingClientRect();

            // Set canvas internal size to match display size (1:1, no DPR scaling)
            // This keeps coordinates simple and aligned
            canvas.width = rect.width;
            canvas.height = rect.height;

            draw();
        };

        resizeCanvas();
        window.addEventListener("resize", resizeCanvas);
        return () => window.removeEventListener("resize", resizeCanvas);
    }, [draw]);

    // Mouse handlers
    const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const [lng, lat] = canvasToLatLng(x, y, canvas);
        setMousePos([lng, lat]);
    };

    const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas || activeTool === "none") return;

        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const [lng, lat] = canvasToLatLng(x, y, canvas);

        if (activeTool === "circle") {
            if (currentDrawing.length === 0) {
                setCurrentDrawing([[lng, lat]]);
            } else {
                const radiusKm = haversineDistance(
                    currentDrawing[0][1], currentDrawing[0][0],
                    lat, lng
                );
                setShapes(prev => [...prev, {
                    id: Math.random().toString(36).substr(2, 9),
                    type: "circle",
                    center: currentDrawing[0],
                    radiusKm,
                    color: selectedColor
                }]);
                setCurrentDrawing([]);
                setActiveTool("none");
            }
        } else {
            setCurrentDrawing(prev => [...prev, [lng, lat]]);
        }
    };

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === "Enter" && (activeTool === "polygon" || activeTool === "line")) {
            if (currentDrawing.length >= 2) {
                setShapes(prev => [...prev, {
                    id: Math.random().toString(36).substr(2, 9),
                    type: activeTool,
                    vertices: currentDrawing,
                    color: selectedColor
                }]);
                setCurrentDrawing([]);
                setActiveTool("none");
            }
        } else if (e.key === "Escape") {
            setCurrentDrawing([]);
            setActiveTool("none");
        }
    }, [activeTool, currentDrawing, selectedColor]);

    useEffect(() => {
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [handleKeyDown]);

    const startDrawing = (tool: DrawingTool) => {
        setActiveTool(tool);
        setCurrentDrawing([]);
    };

    const deleteShape = (id: string) => {
        setShapes(prev => prev.filter(s => s.id !== id));
    };

    const clearAll = () => {
        setShapes([]);
        setCurrentDrawing([]);
        setActiveTool("none");
    };

    return (
        <div className="h-screen flex flex-col pt-36">
            <div className="bg-white border-b border-gray-200 px-6 py-4">
                <h1 className="text-2xl font-semibold text-gray-800">Simple Drawing Tool</h1>
                <p className="text-sm text-gray-600 mt-1">Draw geographic shapes with Haversine calculations</p>
            </div>

            <div className="flex-1 flex overflow-hidden">
                <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
                    <div className="p-4 border-b border-gray-100">
                        <h2 className="font-semibold text-gray-800 mb-3">Drawing Tools</h2>
                        <div className="space-y-2">
                            <button
                                onClick={() => startDrawing("circle")}
                                className={`w-full px-4 py-2 rounded-lg font-medium transition-colors ${
                                    activeTool === "circle"
                                        ? "bg-blue-500 text-white"
                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                            >
                                Circle
                            </button>
                            <button
                                onClick={() => startDrawing("polygon")}
                                className={`w-full px-4 py-2 rounded-lg font-medium transition-colors ${
                                    activeTool === "polygon"
                                        ? "bg-blue-500 text-white"
                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                            >
                                Polygon
                            </button>
                            <button
                                onClick={() => startDrawing("line")}
                                className={`w-full px-4 py-2 rounded-lg font-medium transition-colors ${
                                    activeTool === "line"
                                        ? "bg-blue-500 text-white"
                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                            >
                                Line
                            </button>
                        </div>
                    </div>

                    <div className="p-4 border-b border-gray-100">
                        <h2 className="font-semibold text-gray-800 mb-3">Color</h2>
                        <input
                            type="color"
                            value={selectedColor}
                            onChange={(e) => setSelectedColor(e.target.value)}
                            className="w-full h-10 rounded cursor-pointer"
                        />
                    </div>

                    <div className="flex-1 overflow-y-auto p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="font-semibold text-gray-800">Shapes ({shapes.length})</h2>
                            {shapes.length > 0 && (
                                <button
                                    onClick={clearAll}
                                    className="text-sm text-red-600 hover:text-red-700"
                                >
                                    Clear All
                                </button>
                            )}
                        </div>
                        <div className="space-y-2">
                            {shapes.map((shape) => (
                                <div
                                    key={shape.id}
                                    className="flex items-center justify-between p-2 bg-gray-50 rounded"
                                >
                                    <div className="flex items-center gap-2">
                                        <div
                                            className="w-4 h-4 rounded"
                                            style={{backgroundColor: shape.color}}
                                        />
                                        <span className="text-sm capitalize">{shape.type}</span>
                                    </div>
                                    <button
                                        onClick={() => deleteShape(shape.id)}
                                        className="text-red-600 hover:text-red-700"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="p-4 border-t border-gray-100">
                        <h2 className="font-semibold text-gray-800 mb-3">Test Circles</h2>
                        <div className="space-y-2">
                            <button
                                onClick={() => {
                                    setShapes(prev => [...prev, {
                                        id: Math.random().toString(36).substr(2, 9),
                                        type: "circle",
                                        center: [0, 80],
                                        radiusKm: 1200,
                                        color: "#3B82F6"
                                    }]);
                                }}
                                className="w-full px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded text-left"
                            >
                                North Pole (80°, 1200km)
                            </button>
                            <button
                                onClick={() => {
                                    setShapes(prev => [...prev, {
                                        id: Math.random().toString(36).substr(2, 9),
                                        type: "circle",
                                        center: [0, -80],
                                        radiusKm: 1200,
                                        color: "#EF4444"
                                    }]);
                                }}
                                className="w-full px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded text-left"
                            >
                                South Pole (-80°, 1200km)
                            </button>
                            <button
                                onClick={() => {
                                    setShapes(prev => [...prev, {
                                        id: Math.random().toString(36).substr(2, 9),
                                        type: "circle",
                                        center: [170, 0],
                                        radiusKm: 2000,
                                        color: "#10B981"
                                    }]);
                                }}
                                className="w-full px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded text-left"
                            >
                                Date Line (170°, 2000km)
                            </button>
                            <button
                                onClick={() => {
                                    setShapes(prev => [...prev, {
                                        id: Math.random().toString(36).substr(2, 9),
                                        type: "circle",
                                        center: [0, 0],
                                        radiusKm: 5000,
                                        color: "#F59E0B"
                                    }]);
                                }}
                                className="w-full px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded text-left"
                            >
                                Equator Large (0°, 5000km)
                            </button>
                        </div>
                    </div>

                    <div className="p-4 border-t border-gray-100 text-xs text-gray-500">
                        <p className="font-semibold mb-2">Instructions:</p>
                        <ul className="space-y-1">
                            <li>• Circle: Click center, then click edge</li>
                            <li>• Polygon: Click points, press Enter</li>
                            <li>• Line: Click points, press Enter</li>
                            <li>• Press Escape to cancel</li>
                        </ul>
                    </div>
                </div>

                <div className="flex-1 flex items-center justify-center bg-gray-100 p-8">
                    <div className="relative" style={{
                        width: "100%",
                        maxWidth: "1200px",
                        aspectRatio: "2 / 1"
                    }}>
                        <canvas
                            ref={canvasRef}
                            onClick={handleClick}
                            onMouseMove={handleMouseMove}
                            className="w-full h-full cursor-crosshair border-4 border-gray-800 rounded shadow-lg"
                            style={{
                                aspectRatio: "2 / 1"
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
